// @ts-nocheck

import * as Sentry from '@sentry/react';
import { SubmissionError, change } from 'redux-form';

import networkActions from '../actions/network';
import { setLicence as setRuntimeLicence, setOverrideSettings } from '../actions/runtime';
import userActionCreators from '../actions/user';
import Client, { Target, isIOSEmbeddedAndStandalone } from '../client';
import config from '../config/app.config';
import { RedirectServices, getRedirectUrl } from '../config/urls';
import Formatter from '../i18n/formatter';
import { __, getLang, getLangLong } from '../i18n/l10n';
import { isEmbedded } from '../lib/useragent';
import { getAuthManager } from '../managers/authManager';
import { AuthMessages } from '../types/auth';
import { Licences, Locales, LocalesBasename, AuthorizedLicences } from '../types/common';
import { LoginErrorCodes } from '../types/login';
import { formatQuerystringParams, getCustomHeaders } from '../utils';
import { makeWapiCall } from '../utils/api';
import AuthBettingPlugin from '../utils/plugins/authBetting';
import saveLicenceOnStorage from '../utils/saveLicenceOnStorage';
import saveSocketOnStorage from '../utils/saveSocketOnStorage';
import { saveUserhasConnected } from '../utils/userStorage';

import apiActionCreators from './api';
import { externalLinkOpen } from './externallink';
import { sendReport } from './report';

const generateErrorObject = (fields, estr) =>
  fields.reduce((prev, curr) => {
    prev[curr] = estr;
    return prev;
  }, {});

const callApi = (options) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(
      apiActionCreators.makeApiCall({
        options,
        hooks: {
          success: (response) => {
            resolve(response);
          },
          failure: (error) => {
            reject(error);
          },
        },
      }),
    );
  });
};

const asyncCall = async (form, dispatch) => {
  let response = null;

  try {
    response = await dispatch(
      callApi({
        url: config.standaloneLoginUrl[getLang()],
        method: 'POST',
        credentials: 'same-origin',
        body: form,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...getCustomHeaders({ tracker: Client.tracker }),
        },
      }),
    );
    return response;
  } catch (err) {
    dispatch(
      sendReport({
        error: err.toString(),
        extra: { context: 'login', opid: window.getAppOpid?.() },
      }),
    );

    Sentry.captureException(err, { extra: { context: 'login', opid: window.getAppOpid?.() } });
    // eslint-disable-next-line sonarjs/no-duplicate-string
    throw new SubmissionError(generateErrorObject(['_error', '_code'], __('General problem')));
  }
};

export const submitLoginJwt = (jwt, expirationDelay, licence, context) => async (dispatch) => {
  const data = {
    jwt,
  };
  dispatch(userActionCreators.setConnecting(true));
  try {
    await submitRequest(data, licence, dispatch, context);
    getAuthManager().initWithJWT(jwt, expirationDelay);
  } catch (e) {
    dispatch(userActionCreators.setConnecting(false));
    getAuthManager().logout();
    throw e;
  }

  dispatch(userActionCreators.setConnecting(false));
};

// redux-form on submit function
export const submitLogin = async (formValues, dispatch) => {
  const { licence, ...rest } = formValues;
  const data = { ...rest };

  await submitRequest(data, licence, dispatch, null);
};

const submitRequest = async (data, licence, dispatch, context) => {
  const authorizedLicenses = [...AuthorizedLicences];

  if (data.pincode !== undefined) {
    if (data.pincode === null) {
      data.pincode = '';
    }
  }

  if (isIOSEmbeddedAndStandalone) {
    data.itoken = window.getPushToken();
    data.opid = window.getAppOpid?.();
    data.display = window.navigator.userAgent.indexOf('iPad') !== -1 ? 'tablet' : 'mobile';
    data.cid = window.getAppClientId();
  }

  if (Client.store) {
    data.store = Client.store;
  }
  if (Client.storeId) {
    data.storeId = Client.storeId;
  }
  data.lang = getLang();

  if (context) {
    data.context = context;
  }

  const form = `params=${encodeURIComponent(JSON.stringify(data))}`;
  let response = await asyncCall(form, dispatch);

  // we retry on another licence if we fail on current licence
  if (isIOSEmbeddedAndStandalone && response?.ecode === LoginErrorCodes.INVALID_LANGUAGE) {
    const userLicenceResponse = response.license;
    const userLicence = userLicenceResponse ? Licences[userLicenceResponse] : null;

    if (userLicence) {
      dispatch(setRuntimeLicence(userLicence));
      response = await asyncCall(form, dispatch);
      await handleResponse(response, dispatch);
    } else {
      const currLicenceIndex = authorizedLicenses.indexOf(licence);

      if (currLicenceIndex) {
        authorizedLicenses.splice(currLicenceIndex, 1);
      }

      let newLicence = null;

      while ((newLicence = authorizedLicenses.pop()) && response.ecode === LoginErrorCodes.INVALID_LANGUAGE) {
        dispatch(setRuntimeLicence(newLicence));
        response = await asyncCall(form, dispatch);
        await handleResponse(response, dispatch);
      }
    }
  } else {
    await handleResponse(response, dispatch);
  }
};

const handleResponse = async (response, dispatch) => {
  if (response) {
    if (response.ecode !== 0) {
      dispatch(userActionCreators.setCC(null));
      dispatch(userActionCreators.setSessionId(null));

      switch (response.ecode) {
        case LoginErrorCodes.INVALID_BIOMETRIC_TOKEN:
          throw new SubmissionError({});
        case LoginErrorCodes.NEED_PINCODE:
          throw new SubmissionError({});
        case LoginErrorCodes.INVALID_EMAIL:
        case LoginErrorCodes.INVALID_PASSWORD:
        case LoginErrorCodes.INVALID_OLD_PASSWORD:
          throw new SubmissionError(
            generateErrorObject(
              ['_error', 'email', 'password', 'birthDay', 'birthMonth', 'birthYear'],
              __('Incorrect email, password or date of birth'),
            ),
          );
        case LoginErrorCodes.ACCOUNT_NOT_ACTIVATED:
          throw new SubmissionError(
            generateErrorObject(['_error'], __('Your email address has not been verified. Please check your emails.')),
          );
        case LoginErrorCodes.ACCOUNT_LOCKED:
        case LoginErrorCodes.ACCOUNT_SELF_EXCLUDED:
          throw new SubmissionError(generateErrorObject(['_error'], __('Your account is frozen.')));
        case LoginErrorCodes.ACCOUNT_RESET:
          throw new SubmissionError(
            generateErrorObject(
              ['_error'],
              __('You have made a request to change your password. Please check your emails.'),
            ),
          );
        case LoginErrorCodes.INVALID_BIRTH_DATE:
          throw new SubmissionError(
            generateErrorObject(
              ['_error', 'birthDay', 'birthMonth', 'birthYear'],
              __('The date of birth you have entered is invalid'),
            ),
          );
        case LoginErrorCodes.MANDATORY_FIELD:
          throw new SubmissionError(
            generateErrorObject(
              ['_error', 'birthDay', 'birthMonth', 'birthYear'],
              __('Please enter your date of birth'),
            ),
          );
        case LoginErrorCodes.SHOULD_BE_OVER18:
          throw new SubmissionError(generateErrorObject(['_error'], __('You must be of majority age to play')));
        case LoginErrorCodes.INCORRECT_BIRTH_DATE:
          throw new SubmissionError(
            generateErrorObject(
              ['_error', 'birthDay', 'birthMonth', 'birthYear'],
              __('The date of birth entered does not match the date registered on your account'),
            ),
          );
        case LoginErrorCodes.INVALID_LANGUAGE:
          // redirection if needed, in case user correspond to another licence
          if (!isIOSEmbeddedAndStandalone) {
            const userLicenceResponse = response.license;
            const userLicence = userLicenceResponse ? Licences[userLicenceResponse] : null;

            if (userLicence) {
              const userLanguage = Locales[userLicence];
              const link = config.webHost[userLanguage];
              const params = {};

              if (isEmbedded) {
                params.forceEmbed = true;
              }

              if (response.gtoken) {
                params.gtoken = response.gtoken;
              }

              const paramsString = formatQuerystringParams(params);
              const buttonLink = `${link}/${LocalesBasename[userLanguage]}${paramsString}#login`;
              const linkLabel = link.replace('https://', '') || 'www.winamax.fr';

              throw new SubmissionError(
                generateErrorObject(['_error'], {
                  code: response.ecode,
                  meta: {
                    label: __('You must log in to %1$s in order to access your account.', [linkLabel]),
                    buttonLabel: __('Log in to %1$s', [linkLabel]),
                    buttonLink,
                  },
                }),
              );
            }
          }
          throw new SubmissionError(
            generateErrorObject(['_error'], __('Your account is not authorized for use in this region')),
          );
        case LoginErrorCodes.IP_NOT_ALLOWED:
          throw new SubmissionError(
            generateErrorObject(['_error'], __('You are not authorized to connect from this country.')),
          );
        case LoginErrorCodes.INCORRECT_PINCODE:
          dispatch(change('login', 'pincode', ''));
          throw new SubmissionError(generateErrorObject(['_error'], __('Incorrect PIN')));
        case LoginErrorCodes.PASSWORD_BLOCKED:
          throw new SubmissionError(generateErrorObject(['_error'], __('Too many attempts.')));
        case LoginErrorCodes.MAINTENANCE:
          throw new SubmissionError(
            generateErrorObject(['_error'], {
              code: response.ecode,
              meta: {
                title: __('Maintenance in progress'),
                /* __('The service is currently unavailable. We will restore this as soon as possible. We apologise for any inconveniences caused by this.') */
                label: __(
                  'The service is currently unavailable. We will restore this as soon as possible. We apologise for any inconveniences caused by this.',
                ),
              },
            }),
          );
        case LoginErrorCodes.ACCOUNT_UNKNOWN_ERROR:
        default:
          Sentry.captureMessage(`Unknown error. Code: ${response.ecode}`);
          throw new SubmissionError(generateErrorObject(['_error', '_code'], __('General problem')));
      }
    } else {
      dispatch(userActionCreators.setConnected());

      dispatch(setRuntimeLicence(response.license));
      dispatch(setOverrideSettings(response.overridesettings));
      saveLicenceOnStorage(response.license);
      saveUserhasConnected();

      if (response.commid) {
        document.cookie = `COMMID=${response.commid}`;
      }

      dispatch(userActionCreators.setCC(response.cc));
      dispatch(userActionCreators.setSessionId(response.sessionid));

      // Close current connection
      dispatch(networkActions.close);
      // Connect to the new one
      const suggestedSocket = {
        host: response.config?.socketIoServer,
        path: response.config?.socketIoPath,
      };
      saveSocketOnStorage(suggestedSocket);
      dispatch(networkActions.connect);

      // Update balance
      if (response.balance) {
        const payload = {};
        if (response.balance.amount !== null && response.balance.amount !== undefined) {
          payload.balance = response.balance.amount;
        }

        if (response.balance.amountCash !== null && response.balance.amountCash !== undefined) {
          payload.balanceCash = response.balance.amountCash;
        }

        if (response.balance.amountBonus !== null && response.balance.amountBonus !== undefined) {
          payload.balanceBonus = response.balance.amountBonus;
        }

        if (response.balance.amountFreebet !== null && response.balance.amountFreebet !== undefined) {
          payload.freebetBankroll = response.balance.amountFreebet;
        }

        if (Object.keys(payload).length > 0) {
          dispatch(userActionCreators.setBalance(payload));
        }

        if (response.balance.type) {
          dispatch(userActionCreators.setRealMoney(response.balance.type === 'real'));
        }
      }

      // Receive nickname
      if (response.user && response.user.nickname !== null && response.user.nickname !== undefined) {
        // Set a tag in sentry for nickname
        Sentry.setTag('nickname', response.user.nickname);

        dispatch(userActionCreators.setNickname(response.user.nickname));
      }

      // Receive last session settled bets
      if (response.lastsessionsettledbets) {
        dispatch(userActionCreators.setLastSessionSettledBets(response.lastsessionsettledbets));
      }

      // Prompt the CGU
      if (response.terms) {
        dispatch(userActionCreators.setMustValidateCGU());
      }

      // Show arjel event modal if there are some events to validate
      if (response.events) {
        dispatch(userActionCreators.setMustValidateEvents());
      }

      // User account related notification
      if (Array.isArray(response.notifications) && response.notifications.length > 0 && !Target.__LIBRARY__) {
        dispatch(userActionCreators.setMessages(response.notifications));
      }
    }
  } else {
    Sentry.captureMessage(`No response received. Response: ${JSON.stringify(response)}`);
    throw new SubmissionError(generateErrorObject(['_error', '_code'], __('General problem')));
  }
};

// logout async
const doLogoutAsync = async (dispatch, { jwt, sessionId, cc, disableAuthPlugin = false }) => {
  let response = null;

  try {
    if (Client.embed && !disableAuthPlugin) {
      AuthBettingPlugin.logout();
    }

    response = await dispatch(
      callApi({
        url: config.standaloneLogoutUrl[getLang()],
        method: 'GET',
        credentials: 'same-origin',
        headers: getCustomHeaders({ jwt, sessionId, cc, tracker: Client.tracker }),
      }),
    );
  } catch (err) {
    console.debug(err);
  }

  if (response && response.ecode === 0) {
    dispatch(userActionCreators.setDisconnected());

    // User account related notification
    if (Array.isArray(response.notifications) && response.notifications.length > 0) {
      dispatch(userActionCreators.setMessages(response.notifications));
    }

    getAuthManager().logout();
  }
};

// logout action
const doLogout = (data) => (dispatch) => {
  doLogoutAsync(dispatch, data);
};

export const doLoginEmbedded =
  ({ licence, onLoginDismissed }) =>
  async (dispatch) => {
    try {
      const { token, expiration_delay: expirationDelay, target } = (await AuthBettingPlugin.login(getLangLong())) ?? {};

      if (onLoginDismissed) {
        onLoginDismissed();
      }
      if (token && expirationDelay) {
        await submitLoginJwt(token, expirationDelay, licence, 'login-embedded')(dispatch);
      } else if (target) {
        if (target === AuthMessages.REGISTER) {
          const registerUrl = getRedirectUrl(RedirectServices.REGISTER, { mobile: 1 });
          dispatch(externalLinkOpen(registerUrl, { delay: 300, auth: true }));
        } else if (target === AuthMessages.FORGOTPASSWORD) {
          const forgottenPasswordUrl = getRedirectUrl(RedirectServices.LOST_PASSWORD, { must_logout: 1 });
          dispatch(externalLinkOpen(forgottenPasswordUrl, { delay: 300, auth: true }));
        }
      }
    } catch (e) {
      console.log('Error in auth plugin: ', e?.message);
      const loginError = e?.errors?._error;
      Sentry.captureException(e, { extra: { _error: loginError } });
      let actions = [];
      let [title, text] = [undefined, __('General problem')];

      if (typeof loginError === 'object') {
        const { code, meta } = loginError;
        const { label: metaLabel, title: metaTitle } = meta;

        if (code === LoginErrorCodes.INVALID_LANGUAGE) {
          const { buttonLink, buttonLabel } = meta;

          if (buttonLink && buttonLabel) {
            actions = [{ label: buttonLabel, type: 'link', action: buttonLink }];
          }
        }
        if (metaLabel) {
          text = metaLabel;
        }
        title = metaTitle;
      } else {
        text = loginError;
      }

      dispatch(userActionCreators.setMessages([{ title, text, actions, dismissable: true }]));
      getAuthManager().logout();
    }
  };

const tryLoginEmbedded = (licence, context) => async (dispatch) => {
  try {
    const { token, expiration_delay: expirationDelay } = (await AuthBettingPlugin.loginAuto(getLangLong())) ?? {};

    if (token && expirationDelay) {
      await submitLoginJwt(token, expirationDelay, licence, context)(dispatch);
      return true;
    }
  } catch (e) {
    console.log('Error in auth plugin: ', e?.message);
    Sentry.captureException(e);
  }

  return false;
};

const banDeUser =
  ({ jwt, sessionId }) =>
  async (dispatch) => {
    try {
      if (!jwt) {
        console.error('banDeUser - Cannot call BanOasis without jwt token');
        return;
      }
      const response = await makeWapiCall({
        namespace: 'operator/regulation/germany',
        method: 'BanOasis',
        params: { jwt },
      });

      if (response?.success) {
        const tomorrow = Date.now() / 1000 + 24 * 60 * 60;

        dispatch(
          userActionCreators.setMessages([
            {
              title: __('!desc:PanicButtonConfirmationTitle'),
              text: __('!desc:PanicButtonConfirmation', Formatter.formatDate(tomorrow, 'timeShort')),
              dismissable: true,
            },
          ]),
        );
        doLogout({ jwt, sessionId })(dispatch);
      } else {
        dispatch(
          userActionCreators.setMessages([
            {
              text: __('An error occurred, please try again.'),
              dismissable: true,
            },
          ]),
        );
      }
    } catch (e) {
      console.error('Error trying to self ban', e);
    }
  };

const actions = {
  submitLogin,
  submitLoginJwt,
  doLoginEmbedded,
  tryLoginEmbedded,
  doLogout,
  banDeUser,
};

export { actions };
